import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AVAILABLE_COLUMNS } from '@/lib/listViewColumns';
import type { InventoryShortcutId } from '@/components/inventory/inventoryHelpers';

export type SortConfig = {
	column: string;
	direction: 'asc' | 'desc';
} | null;

interface UIStore {
	viewMode: 'all' | 'collection' | 'sets';
	displayMode: 'grid' | 'list';
	useExperimentalListView: boolean;
	isFilterPanelCollapsed: boolean;
	isFilterPanelTransitioning: boolean;
	isViewModeTransitioning: boolean;
	isDeckBuilderTransitioning: boolean;
	isDeckBuilderActive: boolean;
	isMarketplaceFilterPanelCollapsed: boolean;
	isMarketFilterPanelCollapsed: boolean;
	marketplaceViewMode: 'browse' | 'my-items';
	inventoryDisplayMode: 'grid' | 'list';
	visibleColumns: string[];
	sortConfig: SortConfig;
	microbarMode: 'collection' | 'deck';
	hasSeenCollectionImportPrompt: boolean;
	lastViewedDeckTab: 'my-decks' | 'user-submitted' | null;
	openMicrobarCardId: string | null;
	addItemModalOpen: boolean;
	addItemModalInitialMode: 'item' | 'lot';
	addItemModalDefaultInvestmentOpen: boolean;
	inventorySelectionRequested: boolean;
	pendingEditItemId: string | null;
	collectrImportRequested: boolean;
	stockImageReviewRequested: boolean;
	itemsWithoutImagesCount: number;
	rapidListRequested: boolean;
	bulkPhotoUploadRequested: boolean;
	rapidPhotographRequested: boolean;
	activeInventoryShortcut: InventoryShortcutId;
	pendingSalesQueueOpen: boolean;


	setViewMode: (mode: 'all' | 'collection' | 'sets') => void;
	setDisplayMode: (mode: 'grid' | 'list') => void;
	toggleExperimentalListView: () => void;
	toggleFilterPanel: () => void;
	toggleMarketplaceFilterPanel: () => void;
	toggleMarketFilterPanel: () => void;
	setFilterPanelTransitioning: (isTransitioning: boolean) => void;
	setViewModeTransitioning: (isTransitioning: boolean) => void;
	setDeckBuilderTransitioning: (isTransitioning: boolean) => void;
	setDeckBuilderActive: (isActive: boolean) => void;
	toggleColumn: (columnId: string) => void;
	isColumnVisible: (columnId: string) => boolean;
	resetColumnsToDefaults: () => void;
	handleColumnSort: (columnId: string) => void;
	clearSort: () => void;
	setMicrobarMode: (mode: 'collection' | 'deck') => void;
	setHasSeenCollectionImportPrompt: (seen: boolean) => void;
	setLastViewedDeckTab: (tab: 'my-decks' | 'user-submitted') => void;
	setOpenMicrobarCardId: (cardId: string | null) => void;
	openAddItemModal: () => void;
	openAddItemModalWithInvestment: () => void;
	openAddLotModal: () => void;
	closeAddItemModal: () => void;

	requestInventorySelection: () => void;
	clearInventorySelectionRequest: () => void;
	requestEditItem: (itemId: string) => void;
	clearEditItemRequest: () => void;
	requestCollectrImport: () => void;
	clearCollectrImportRequest: () => void;
	requestStockImageReview: () => void;
	clearStockImageReviewRequest: () => void;
	setItemsWithoutImagesCount: (count: number) => void;
	requestRapidList: () => void;
	clearRapidListRequest: () => void;
	requestBulkPhotoUpload: () => void;
	clearBulkPhotoUploadRequest: () => void;
	requestRapidPhotograph: () => void;
	clearRapidPhotographRequest: () => void;
	setActiveInventoryShortcut: (id: InventoryShortcutId) => void;
	setMarketplaceViewMode: (mode: 'browse' | 'my-items') => void;
	setPendingSalesQueueOpen: (open: boolean) => void;
	setInventoryDisplayMode: (mode: 'grid' | 'list') => void;
}

export const useUIStore = create<UIStore>()(
	persist(
		(set, get) => ({
			viewMode: 'all',
			displayMode: 'grid',
			useExperimentalListView: false,
			isFilterPanelCollapsed: false,
			isFilterPanelTransitioning: false,
			isViewModeTransitioning: false,
			isDeckBuilderTransitioning: false,
			isDeckBuilderActive: false,
			isMarketplaceFilterPanelCollapsed: false,
			isMarketFilterPanelCollapsed: false,
			marketplaceViewMode: 'browse',
			inventoryDisplayMode: 'grid',
			visibleColumns: AVAILABLE_COLUMNS.filter((col) => col.defaultVisible).map((col) => col.id),
			sortConfig: null,
			microbarMode: 'collection',
			hasSeenCollectionImportPrompt: false,
			lastViewedDeckTab: null,
			openMicrobarCardId: null,
			addItemModalOpen: false,
			addItemModalInitialMode: 'item',
			addItemModalDefaultInvestmentOpen: false,
		inventorySelectionRequested: false,
		pendingEditItemId: null,
		collectrImportRequested: false,
			stockImageReviewRequested: false,
			itemsWithoutImagesCount: 0,
			rapidListRequested: false,
			bulkPhotoUploadRequested: false,
			rapidPhotographRequested: false,
			activeInventoryShortcut: 'all',
			pendingSalesQueueOpen: false,


			setViewMode: (mode) => set({ viewMode: mode }),
			setDisplayMode: (mode) => set({ displayMode: mode }),
			setMicrobarMode: (mode) => set({ microbarMode: mode }),
			setHasSeenCollectionImportPrompt: (seen: boolean) =>
				set({ hasSeenCollectionImportPrompt: seen }),
			setLastViewedDeckTab: (tab) => set({ lastViewedDeckTab: tab }),
			setOpenMicrobarCardId: (cardId) => set({ openMicrobarCardId: cardId }),
			openAddItemModal: () => set({ addItemModalOpen: true, addItemModalInitialMode: 'item', addItemModalDefaultInvestmentOpen: false }),
			openAddItemModalWithInvestment: () => set({ addItemModalOpen: true, addItemModalInitialMode: 'item', addItemModalDefaultInvestmentOpen: true }),
			openAddLotModal: () => set({ addItemModalOpen: true, addItemModalInitialMode: 'lot', addItemModalDefaultInvestmentOpen: false }),
			closeAddItemModal: () => set({ addItemModalOpen: false, addItemModalInitialMode: 'item', addItemModalDefaultInvestmentOpen: false }),

		requestInventorySelection: () => set({ inventorySelectionRequested: true }),
		clearInventorySelectionRequest: () => set({ inventorySelectionRequested: false }),
		requestEditItem: (itemId: string) => set({ pendingEditItemId: itemId }),
		clearEditItemRequest: () => set({ pendingEditItemId: null }),
		requestCollectrImport: () => set({ collectrImportRequested: true }),
			clearCollectrImportRequest: () => set({ collectrImportRequested: false }),
			requestStockImageReview: () => set({ stockImageReviewRequested: true }),
			clearStockImageReviewRequest: () => set({ stockImageReviewRequested: false }),
			setItemsWithoutImagesCount: (count: number) => set({ itemsWithoutImagesCount: count }),
			requestRapidList: () => set({ rapidListRequested: true }),
			clearRapidListRequest: () => set({ rapidListRequested: false }),
			requestBulkPhotoUpload: () => set({ bulkPhotoUploadRequested: true }),
			clearBulkPhotoUploadRequest: () => set({ bulkPhotoUploadRequested: false }),
			requestRapidPhotograph: () => set({ rapidPhotographRequested: true }),
			clearRapidPhotographRequest: () => set({ rapidPhotographRequested: false }),
			setActiveInventoryShortcut: (id) => set({ activeInventoryShortcut: id }),
			setPendingSalesQueueOpen: (open) => set({ pendingSalesQueueOpen: open }),
			toggleExperimentalListView: () =>
				set((state) => ({ useExperimentalListView: !state.useExperimentalListView })),
			toggleFilterPanel: () =>
				set((state) => ({ isFilterPanelCollapsed: !state.isFilterPanelCollapsed })),
			toggleMarketplaceFilterPanel: () =>
				set((state) => ({ isMarketplaceFilterPanelCollapsed: !state.isMarketplaceFilterPanelCollapsed })),
			toggleMarketFilterPanel: () =>
				set((state) => ({ isMarketFilterPanelCollapsed: !state.isMarketFilterPanelCollapsed })),
			setFilterPanelTransitioning: (isTransitioning: boolean) =>
				set({ isFilterPanelTransitioning: isTransitioning }),
			setViewModeTransitioning: (isTransitioning: boolean) =>
				set({ isViewModeTransitioning: isTransitioning }),
			setDeckBuilderTransitioning: (isTransitioning: boolean) =>
				set({ isDeckBuilderTransitioning: isTransitioning }),
			setDeckBuilderActive: (isActive: boolean) => set({ isDeckBuilderActive: isActive }),
			setMarketplaceViewMode: (mode) => set({ marketplaceViewMode: mode }),
			setInventoryDisplayMode: (mode) => set({ inventoryDisplayMode: mode }),

			toggleColumn: (columnId: string) => {
				const state = get();
				const current = state.visibleColumns;
				if (current.includes(columnId)) {
					if (current.length === 1) return;
					set({ visibleColumns: current.filter((id) => id !== columnId) });
				} else {
					set({ visibleColumns: [...current, columnId] });
				}
			},

			isColumnVisible: (columnId: string) => {
				const exists = AVAILABLE_COLUMNS.some((c) => c.id === columnId);
				return exists && get().visibleColumns.includes(columnId);
			},

			resetColumnsToDefaults: () => {
				const defaults = AVAILABLE_COLUMNS.filter((col) => col.defaultVisible).map((col) => col.id);
				set({ visibleColumns: defaults });
			},

			handleColumnSort: (columnId: string) => {
				const column = AVAILABLE_COLUMNS.find((col) => col.id === columnId);
				if (!column?.sortable) return;

				const currentSort = get().sortConfig;
				if (currentSort?.column === columnId) {
					if (currentSort.direction === 'asc') {
						set({ sortConfig: { column: columnId, direction: 'desc' } });
					} else {
						set({ sortConfig: null });
					}
				} else {
					set({ sortConfig: { column: columnId, direction: 'asc' } });
				}
			},

			clearSort: () => {
				set({ sortConfig: null });
			}
		}),
		{
			name: 'ui-storage',
			partialize: (state) => ({
				viewMode: state.viewMode,
				displayMode: state.displayMode,
				useExperimentalListView: state.useExperimentalListView,
				isFilterPanelCollapsed: state.isFilterPanelCollapsed,
				isMarketplaceFilterPanelCollapsed: state.isMarketplaceFilterPanelCollapsed,
				isMarketFilterPanelCollapsed: state.isMarketFilterPanelCollapsed,
				marketplaceViewMode: state.marketplaceViewMode,
				inventoryDisplayMode: state.inventoryDisplayMode,
				visibleColumns: state.visibleColumns,
				sortConfig: state.sortConfig,
				microbarMode: state.microbarMode,
				hasSeenCollectionImportPrompt: state.hasSeenCollectionImportPrompt,
				lastViewedDeckTab: state.lastViewedDeckTab,
				openMicrobarCardId: state.openMicrobarCardId
			})
		}
	)
);
